import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  askAssistant,
  cancelAssistantTurn,
  createAssistantConversation,
  deleteAssistantConversation,
} from '@/api/assistant/assistantApi';
import type {
  AssistantMessage,
  AssistantPageContext,
  AssistantStreamEvent,
  AssistantToolActivity,
  AssistantTurnOutcome,
  AssistantUiAction,
} from '@/api/assistant/assistantApi.types';
import { streamAssistantTurn } from '@/api/assistant/assistantTurnStream';
import { startAssistantBrief } from '@/api/assistantProactive/assistantProactiveApi';
import type { AssistantBriefKind } from '@/api/assistantProactive/assistantProactiveApi.types';
import { readCurrentLocalMoment } from '@/utils/localMoment';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { ASSISTANT_STREAM_INTERRUPTED_ERROR_CODE } from '@/constants/assistant';
import {
  ASSISTANT_CONVERSATIONS_QUERY_KEY,
  buildAssistantMessagesQueryKey,
} from '@/constants/queryKeys';

export type AssistantLiveTurnStatus = 'asking' | 'streaming' | 'completed' | 'failed';

export interface AssistantLiveTurn {
  generation: number;
  conversationId: string | null;
  turnId: string | null;
  userMessage: AssistantMessage | null;
  question: string;
  text: string;
  activities: AssistantToolActivity[];
  actionsAwaitingConfirmation: AssistantUiAction[];
  automaticActionResult: AssistantAutomaticActionResult | null;
  status: AssistantLiveTurnStatus;
  outcome: AssistantTurnOutcome | null;
  errorCode: string | null;
  errorMessage: string;
}

export type AssistantAutomaticActionResult =
  'performed' | 'skipped' | 'unsaved_changes' | 'target_missing' | 'unavailable';

export type AssistantUiActionHandler = (
  action: AssistantUiAction,
  turnConversationId: string | null
) => Promise<AssistantAutomaticActionResult>;

interface AssistantTurnOptions {
  conversationId: string | null;
  onConversationCreated: (conversationId: string) => void;
  onUiAction: AssistantUiActionHandler;
}

type LiveTurnUpdate = (current: AssistantLiveTurn) => AssistantLiveTurn;

interface AssistantStartedTurn {
  conversationId: string;
  turnId: string;
  userMessage: AssistantMessage | null;
  isNewConversation: boolean;
}

function upsertActivity(
  activities: AssistantToolActivity[],
  activity: AssistantToolActivity
): AssistantToolActivity[] {
  const others = activities.filter(function differentCall(existing) {
    return existing.callId !== activity.callId;
  });
  return [...others, activity];
}

function initialTurn(
  generation: number,
  conversationId: string | null,
  question: string
): AssistantLiveTurn {
  return {
    generation,
    conversationId,
    turnId: null,
    userMessage: null,
    question,
    text: '',
    activities: [],
    actionsAwaitingConfirmation: [],
    automaticActionResult: null,
    status: 'asking',
    outcome: null,
    errorCode: null,
    errorMessage: '',
  };
}

function applyStreamEvent(current: AssistantLiveTurn, event: AssistantStreamEvent) {
  if (event.type === 'text_delta') {
    return { ...current, status: 'streaming' as const, text: current.text + event.text };
  }
  if (event.type === 'tool_activity') {
    return {
      ...current,
      status: 'streaming' as const,
      activities: upsertActivity(current.activities, event.activity),
    };
  }
  if (event.type === 'turn_completed') {
    return { ...current, status: 'completed' as const, outcome: event.outcome };
  }
  if (event.type === 'turn_failed') {
    return { ...current, status: 'failed' as const, errorCode: event.errorCode };
  }
  return { ...current, status: 'streaming' as const };
}

function isRunning(turn: AssistantLiveTurn): boolean {
  return turn.status === 'asking' || turn.status === 'streaming';
}

function markInterrupted(current: AssistantLiveTurn): AssistantLiveTurn {
  return isRunning(current)
    ? { ...current, status: 'failed', errorCode: ASSISTANT_STREAM_INTERRUPTED_ERROR_CODE }
    : current;
}

function markFailed(sendError: unknown): LiveTurnUpdate {
  const errorMessage = resolveApiErrorMessage(sendError);
  return function applyFailure(current) {
    return {
      ...current,
      status: 'failed',
      errorMessage,
      errorCode: errorMessage.length > 0 ? null : ASSISTANT_STREAM_INTERRUPTED_ERROR_CODE,
    };
  };
}

function markStopped(current: AssistantLiveTurn): AssistantLiveTurn {
  return isRunning(current) ? { ...current, status: 'completed', outcome: 'cancelled' } : current;
}

function cancelQuietly(turnId: string | null): void {
  if (turnId === null) {
    return;
  }
  cancelAssistantTurn(turnId).catch(function ignoreCancelFailure(cancelError: unknown) {
    void cancelError;
  });
}

function deleteQuietly(conversationId: string): void {
  deleteAssistantConversation(conversationId).catch(function ignoreDeleteFailure(
    deleteError: unknown
  ) {
    void deleteError;
  });
}

export function useAssistantTurn({
  conversationId,
  onConversationCreated,
  onUiAction,
}: AssistantTurnOptions) {
  const queryClient = useQueryClient();
  const [liveTurn, setLiveTurn] = useState<AssistantLiveTurn | null>(null);
  const generationRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeTurnIdRef = useRef<string | null>(null);
  const onUiActionRef = useRef(onUiAction);
  const onConversationCreatedRef = useRef(onConversationCreated);

  useLayoutEffect(function keepLatestHandlers() {
    onUiActionRef.current = onUiAction;
    onConversationCreatedRef.current = onConversationCreated;
  });

  const releaseRunningTurn = useCallback(function abortAndCancel() {
    if (abortControllerRef.current !== null) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    cancelQuietly(activeTurnIdRef.current);
    activeTurnIdRef.current = null;
  }, []);

  useEffect(
    function abortOnUnmount() {
      return function stopEverything() {
        generationRef.current += 1;
        releaseRunningTurn();
      };
    },
    [releaseRunningTurn]
  );

  const refreshConversation = useCallback(
    async function refresh(activeConversationId: string) {
      await queryClient.invalidateQueries({
        queryKey: buildAssistantMessagesQueryKey(activeConversationId),
      });
      void queryClient.invalidateQueries({ queryKey: ASSISTANT_CONVERSATIONS_QUERY_KEY });
    },
    [queryClient]
  );

  const runTurn = useCallback(
    async function runPreparedTurn(
      question: string,
      startTurn: (isCurrent: () => boolean) => Promise<AssistantStartedTurn | null>
    ): Promise<boolean> {
      if (abortControllerRef.current !== null) {
        return false;
      }
      generationRef.current += 1;
      const generation = generationRef.current;
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      function isCurrent(): boolean {
        return generationRef.current === generation;
      }
      function updateCurrent(update: LiveTurnUpdate) {
        setLiveTurn(function applyIfCurrent(current) {
          if (current === null || current.generation !== generation) {
            return current;
          }
          return update(current);
        });
      }
      setLiveTurn(initialTurn(generation, conversationId, question));
      let startedTurn: AssistantStartedTurn | null = null;
      try {
        startedTurn = await startTurn(isCurrent);
        if (startedTurn === null) {
          return false;
        }
        const acceptedTurn = startedTurn;
        activeTurnIdRef.current = acceptedTurn.turnId;
        if (isCurrent() === false || abortController.signal.aborted) {
          cancelQuietly(acceptedTurn.turnId);
          updateCurrent(markStopped);
          return true;
        }
        updateCurrent(function recordTurn(current) {
          return {
            ...current,
            conversationId: acceptedTurn.conversationId,
            turnId: acceptedTurn.turnId,
            userMessage: acceptedTurn.userMessage,
          };
        });
        if (acceptedTurn.isNewConversation) {
          onConversationCreatedRef.current(acceptedTurn.conversationId);
        }
        await streamAssistantTurn(
          acceptedTurn.turnId,
          function handleEvent(event) {
            if (isCurrent() === false) {
              return;
            }
            if (event.type !== 'ui_action') {
              updateCurrent(function applyEvent(current) {
                return applyStreamEvent(current, event);
              });
              return;
            }
            void onUiActionRef
              .current(event.action, acceptedTurn.conversationId)
              .then(function recordOutcome(result) {
                updateCurrent(function recordActionResult(current) {
                  if (result === 'performed') {
                    return { ...current, automaticActionResult: result };
                  }
                  return {
                    ...current,
                    automaticActionResult: result,
                    actionsAwaitingConfirmation: [
                      ...current.actionsAwaitingConfirmation,
                      event.action,
                    ],
                  };
                });
              });
          },
          abortController.signal
        );
        updateCurrent(markInterrupted);
      } catch (sendError) {
        if (abortController.signal.aborted) {
          updateCurrent(markStopped);
        } else {
          updateCurrent(markFailed(sendError));
          cancelQuietly(activeTurnIdRef.current);
        }
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
          activeTurnIdRef.current = null;
        }
      }
      if (startedTurn !== null) {
        await refreshConversation(startedTurn.conversationId);
      }
      return startedTurn !== null;
    },
    [conversationId, refreshConversation]
  );

  const send = useCallback(
    function sendQuestion(question: string, pageContext: AssistantPageContext): Promise<boolean> {
      return runTurn(question, async function askInConversation(isCurrent) {
        let activeConversationId = conversationId;
        let isNewConversation = false;
        if (activeConversationId === null) {
          const conversation = await createAssistantConversation();
          activeConversationId = conversation.id;
          isNewConversation = true;
        }
        if (isCurrent() === false) {
          return null;
        }
        try {
          const askResult = await askAssistant(activeConversationId, question, pageContext);
          return {
            conversationId: activeConversationId,
            turnId: askResult.turnId,
            userMessage: askResult.userMessage,
            isNewConversation,
          };
        } catch (askError) {
          if (isNewConversation) {
            deleteQuietly(activeConversationId);
          }
          throw askError;
        }
      });
    },
    [conversationId, runTurn]
  );

  const startBrief = useCallback(
    async function startDailyBrief(kind: AssistantBriefKind): Promise<boolean> {
      const briefStart = await startAssistantBrief(kind, readCurrentLocalMoment());
      const turnId = briefStart.turnId;
      if (turnId === null) {
        onConversationCreatedRef.current(briefStart.conversationId);
        return true;
      }
      const question = briefStart.userMessage === null ? '' : briefStart.userMessage.text;
      return runTurn(question, async function useStartedBrief() {
        return {
          conversationId: briefStart.conversationId,
          turnId,
          userMessage: briefStart.userMessage,
          isNewConversation: true,
        };
      });
    },
    [runTurn]
  );

  const stop = useCallback(
    function stopTurn() {
      releaseRunningTurn();
    },
    [releaseRunningTurn]
  );

  const clear = useCallback(
    function clearTurn() {
      generationRef.current += 1;
      releaseRunningTurn();
      setLiveTurn(null);
    },
    [releaseRunningTurn]
  );

  useEffect(
    function dropTurnOfAnotherConversation() {
      if (liveTurn !== null && liveTurn.conversationId !== conversationId) {
        clear();
      }
    },
    [conversationId, liveTurn, clear]
  );

  const isBusy = liveTurn !== null && isRunning(liveTurn);

  return { liveTurn, isBusy, send, startBrief, stop, clear };
}
